import { Fragment, useEffect, useState } from "react"
import PropTypes from "prop-types"

import DIYTandaSelect from "./diy_tanda_select"
import DIYTandaSelected from "./diy_tanda_selected"

import "./initial_step.scss"
import DIYAddTandaButton from "./diy_add_tanda_button"
import DIYAddFilterQueryButtons from "./diy_add_filter_query_buttons"
import Spinner from "shared/components/spinner"

import {
  trackClickAddFilterCodeButton,
  trackClickAddMoreStepButton,
  trackClickAddQueryButton,
  trackClickContinueButton,
  trackClickFilterCodeEditButton,
  trackClickStepDeleteButton,
  trackStateChangeAppletUpdated,
  trackStateChangeMakeItYourOwn,
  trackClickDelayEditButton,
  trackClickAddDelayButton,
  trackStateChangeDelay,
} from "./utils/telemetry"

import colors from "foundation/_colors_export.scss?variables"

import { editApplet, updateCommunityApplet } from "./api"
import { formatToTimestamp } from "./utils/format_time"
import { attachNewBadgeForButtons } from "shared/scripts/new_badge_button"

const InitialStep = ({
  editing,
  sourceAppletId,
  userOwned,
  dispatch,
  state,
  user,
  userAtQuota,
  makeItYourOwnFeaturePrompt,
  makingItTheirOwn,
  mobileFilterCodeGate,
  proFeatureGate,
  onCreationSuccess,
  showFilterCodeModal,
  hasExistingDelay,
}) => {
  const showProFeatures = user.tier === "pro"
  const triggerAdded = !!state.selectedTrigger
  const queriesAdded = state.selectedQueries.length > 0
  const filterCodeAdded = !!state.filterCode
  const delayAdded = state.delay > 0
  const actionsAdded = state.selectedActions.length > 0
  const [queryFilterButtonsVisible, updateQueryFilterButtons] = useState(false)
  const [saving, updateSaving] = useState(false)

  useEffect(() => {
    const hasChanges = editing || triggerAdded || queriesAdded || filterCodeAdded || actionsAdded || delayAdded

    if (!hasChanges) {
      dispatch({ type: "SET_OVERRIDE_STATE", dataChanged: false })
    }

    attachNewBadgeForButtons()
  }, [editing, triggerAdded, queriesAdded, filterCodeAdded, actionsAdded, delayAdded])

  const onUpdate = ({ errors, normalized_applet }) => {
    const id = normalized_applet?.id
    if (!id || errors?.length) {
      onUpdateFailure(errors)
    } else {
      trackStateChangeAppletUpdated(id)

      if (!hasExistingDelay && delayAdded) {
        trackStateChangeDelay("created")
      } else if (hasExistingDelay && !delayAdded) {
        trackStateChangeDelay("deleted")
      }

      let callback = () => window.history.back()
      if (makingItTheirOwn) {
        trackStateChangeMakeItYourOwn(id)
        callback = () => window.Turbolinks.visit(`/applets/${id}`)
      }

      onCreationSuccess?.()
      window.appendFlash(`Applet ${makingItTheirOwn ? "saved" : "updated"}`, "success", callback)
    }
  }

  const onUpdateFailure = err => {
    updateSaving(false)

    let errorCopy
    if (err?.length && err.some(e => e.attribute === "/applet/filter_code")) {
      dispatch({ type: "FILTER_CODE_ERROR" })
      errorCopy = "Your Applet can't be created because of a filter code error."
    } else {
      errorCopy = "Your Applet can't be created because it hasn't been set up properly."
    }

    console.error(err)
    window.appendFlash(errorCopy, "danger")
  }

  const onClickContinue = () => {
    updateSaving(true)

    const communityApplet = editing && !(userOwned || makingItTheirOwn)

    if (communityApplet) {
      updateCommunityApplet(state)
        .then(({ normalizedAppletEnable }) => onUpdate(normalizedAppletEnable))
        .catch(onUpdateFailure)
    } else if (editing) {
      editApplet({
        appletId: sourceAppletId,
        name: state.name,
        trigger: state.selectedTrigger,
        queries: state.selectedQueries,
        actions: state.selectedActions,
        filterCode: state.filterCode,
        delay: state.delay,
      })
        .then(({ diyAppletEdit }) => onUpdate(diyAppletEdit))
        .catch(onUpdateFailure)
    } else {
      trackClickContinueButton()
      dispatch({ type: "PREVIEW" })
    }
  }

  const disableAddStepFromQuota = userAtQuota && (!editing || makingItTheirOwn)

  return (
    <>
      {state.loading ? (
        <Spinner />
      ) : (
        <>
          <section styleName="diy-block">
            {triggerAdded ? (
              <DIYTandaSelected
                canEdit={state.selectedTrigger?.trigger_fields.length > 0}
                label="if"
                stepName={state.selectedTrigger.name}
                service={state.selectedTrigger.service}
                liveChannelId={state.selectedTrigger.live_channel_id}
                onEditClick={async () => {
                  dispatch({
                    type: "SELECTING_TRIGGER_FIELDS",
                    value: state.selectedTrigger,
                  })
                }}
                onDeleteClick={async dataConfirmation => {
                  trackClickStepDeleteButton({
                    stepIdentifier: state.selectedTrigger.full_module_name,
                    stepType: "trigger",
                  })
                  try {
                    await makeItYourOwnFeaturePrompt()
                    if (queriesAdded || filterCodeAdded || actionsAdded) {
                      await dataConfirmation()
                    }
                    dispatch({ type: "TRIGGER_REMOVED" })
                  } catch (err) {
                    if (!["User needs upgrade", "cancellation"].includes(err)) {
                      throw new Error(err)
                    }
                  }
                }}
                showProFeatures={showProFeatures}
              />
            ) : (
              <DIYTandaSelect
                disabled={disableAddStepFromQuota}
                label="if"
                onClick={() => dispatch({ type: "SELECTING_TRIGGER_SERVICE" })}
              />
            )}
            <div styleName="separator" />

            {queriesAdded && (
              <>
                {state.selectedQueries.map((query, index) => (
                  <Fragment key={index}>
                    <DIYTandaSelected
                      canEdit={query.query_fields.length > 0}
                      label="with"
                      stepName={query.name}
                      service={query.service}
                      liveChannelId={query.live_channel_id}
                      onEditClick={async () => {
                        dispatch({
                          type: "SELECTING_QUERY_FIELDS",
                          value: query,
                          index,
                        })
                      }}
                      onDeleteClick={async dataConfirmation => {
                        trackClickStepDeleteButton({
                          stepIdentifier: query.full_module_name,
                          stepType: "query",
                        })
                        await proFeatureGate("queries")
                        await makeItYourOwnFeaturePrompt()
                        await dataConfirmation()
                        dispatch({
                          type: "QUERY_REMOVED",
                          value: index,
                        })
                      }}
                      showProFeatures={showProFeatures}
                    />
                    <div styleName="separator" />
                  </Fragment>
                ))}
              </>
            )}

            {filterCodeAdded && (
              <>
                <DIYTandaSelected
                  canEdit
                  label="when"
                  stepName="Filter code"
                  service={{
                    brand_color: colors.primaryTextColor,
                    image: "",
                    name: "",
                    allow_multiple_live_channels: false,
                    live_channels: [],
                  }}
                  liveChannelId="0"
                  onEditClick={async () => {
                    trackClickFilterCodeEditButton()
                    await proFeatureGate("filter_code")
                    await mobileFilterCodeGate()
                    await makeItYourOwnFeaturePrompt()
                    dispatch({
                      type: "EDITING_FILTER_CODE",
                    })
                  }}
                  onDeleteClick={async dataConfirmation => {
                    trackClickStepDeleteButton({
                      stepIdentifier: null,
                      stepType: "filter_code",
                    })
                    await proFeatureGate("filter_code")
                    await makeItYourOwnFeaturePrompt()
                    await dataConfirmation()
                    dispatch({
                      type: "FILTER_CODE_REMOVED",
                    })
                  }}
                />
                <div styleName="separator" />
              </>
            )}

            {triggerAdded && (
              <>
                {queryFilterButtonsVisible ? (
                  <>
                    <DIYAddFilterQueryButtons
                      onDelayButtonClick={async () => {
                        trackClickAddDelayButton()
                        await makeItYourOwnFeaturePrompt()
                        dispatch({
                          type: delayAdded ? "EDITING_DELAY_ACTION" : "ADDING_DELAY_ACTION",
                        })
                      }}
                      onQueryButtonClick={async () => {
                        trackClickAddQueryButton()
                        await proFeatureGate("queries")
                        await makeItYourOwnFeaturePrompt()
                        dispatch({ type: "SELECTING_QUERY_SERVICE" })
                      }}
                      onFilterButtonClick={async () => {
                        trackClickAddFilterCodeButton()
                        await proFeatureGate("filter_code")
                        await mobileFilterCodeGate()
                        await makeItYourOwnFeaturePrompt()
                        if (actionsAdded) {
                          dispatch({
                            type: filterCodeAdded ? "EDITING_FILTER_CODE" : "ADDING_FILTER_CODE",
                          })
                        } else {
                          await showFilterCodeModal()
                        }
                      }}
                      filterCodeAdded={filterCodeAdded}
                      delayAdded={delayAdded}
                      userTier={user.tier}
                    />
                  </>
                ) : (
                  <DIYAddTandaButton
                    anchorTitle="Add queries, delay action, or filter code"
                    allowNewBadge="filter_code_queries_delay"
                    onClick={() => {
                      trackClickAddMoreStepButton({ stepType: "trigger" })
                      updateQueryFilterButtons(true)
                    }}
                  />
                )}
              </>
            )}

            {delayAdded && (
              <>
                <div styleName="separator" />
                <DIYTandaSelected
                  canEdit
                  label="wait"
                  stepName={formatToTimestamp(state.delay)}
                  service={{
                    brand_color: colors.primaryTextColor,
                    image: "",
                    name: "",
                  }}
                  onEditClick={async () => {
                    trackClickDelayEditButton()
                    dispatch({
                      type: "EDITING_DELAY_ACTION",
                    })
                  }}
                  onDeleteClick={async () => {
                    trackClickStepDeleteButton({
                      stepIdentifier: null,
                      stepType: "delay",
                    })
                    await makeItYourOwnFeaturePrompt()
                    dispatch({
                      type: "DELAY_ACTION_REMOVED",
                    })
                  }}
                />
              </>
            )}

            {actionsAdded ? (
              <>
                {state.selectedActions.map((action, index) => (
                  <Fragment key={index}>
                    <div styleName="separator" />
                    <DIYTandaSelected
                      canEdit={action.action_fields.length > 0}
                      label={index === 0 ? "then" : "and"}
                      stepName={action.name}
                      service={action.service}
                      liveChannelId={action.live_channel_id}
                      onEditClick={async () => {
                        if (index > 0) {
                          await proFeatureGate("multi_action")
                        }

                        dispatch({
                          type: "SELECTING_ACTION_FIELDS",
                          value: action,
                          index,
                        })
                      }}
                      onDeleteClick={async dataConfirmation => {
                        trackClickStepDeleteButton({
                          stepIdentifier: action.full_module_name,
                          stepType: "action",
                        })

                        if (index > 0) {
                          await proFeatureGate("multi_action")
                        }

                        try {
                          await makeItYourOwnFeaturePrompt()
                          await dataConfirmation()
                          dispatch({
                            type: "ACTION_REMOVED",
                            value: index,
                          })
                        } catch (err) {
                          if (!["User needs upgrade", "cancellation"].includes(err)) {
                            throw new Error(err)
                          }
                        }
                      }}
                      showProFeatures={showProFeatures}
                    />
                  </Fragment>
                ))}
                {triggerAdded && (
                  <>
                    <div styleName="separator" />
                    <DIYAddTandaButton
                      anchorTitle="Add more actions"
                      allowNewBadge="multi_action"
                      onClick={async () => {
                        trackClickAddMoreStepButton({ stepType: "action" })
                        try {
                          await proFeatureGate("multi_action")
                          await makeItYourOwnFeaturePrompt()
                          dispatch({
                            type: "SELECTING_ACTION_SERVICE",
                            value: true,
                          })
                        } catch (err) {
                          if (!["User needs upgrade", "cancellation"].includes(err)) {
                            throw new Error(err)
                          }
                        }
                      }}
                    />
                  </>
                )}
              </>
            ) : (
              <>
                <div styleName="separator" />
                <DIYTandaSelect
                  disabled={!triggerAdded || disableAddStepFromQuota}
                  label="then"
                  onClick={() => dispatch({ type: "SELECTING_ACTION_SERVICE" })}
                />
              </>
            )}
          </section>

          {triggerAdded && actionsAdded && (
            <section styleName="footer">
              <button className="button-primary" onClick={onClickContinue} disabled={!state.dataChanged || saving}>
                {editing ? (makingItTheirOwn ? "Save" : "Update") : "Continue"}
              </button>
            </section>
          )}
        </>
      )}
    </>
  )
}

InitialStep.propTypes = {
  sourceAppletId: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  editing: PropTypes.bool,
  userOwned: PropTypes.bool,
  user: PropTypes.shape({
    tier: PropTypes.string.isRequired,
  }).isRequired,
  state: PropTypes.object.isRequired,
  userAtQuota: PropTypes.bool.isRequired,
  makeItYourOwnFeaturePrompt: PropTypes.func.isRequired,
  makingItTheirOwn: PropTypes.bool,
  mobileFilterCodeGate: PropTypes.func.isRequired,
  proFeatureGate: PropTypes.func.isRequired,
  onCreationSuccess: PropTypes.func,
  showFilterCodeModal: PropTypes.func,
  hasExistingDelay: PropTypes.bool,
}

export default InitialStep
